import clsx from 'clsx';
import { Avatar } from '../ui/avatar';
import { initialsOf, tintOf } from '../lib/mark';

/**
 * The mark for an organisation or a site: its own image when it has one,
 * its initials when it has not.
 *
 * Here rather than at each call site for the reason `lib/mark` itself
 * exists — a card and the switcher that name the same thing must not
 * disagree about how it looks. The initials rule was already shared; the
 * choice between an image and initials is the same kind of rule.
 *
 * What the image IS differs by kind, and neither is this component's
 * business: an organisation stores one in `settings.logo`, a site has
 * its apple touch icon lifted out of its bundle by the sites list.
 *
 * Always decorative (`alt=""`): every place this renders sits next to
 * the name in text.
 */
export function Mark({
  id,
  name,
  image,
  className,
  fallbackClassName,
  plate = true,
}: {
  /** The slug — an org id or a site key. Seeds the fallback tint, so a rename keeps the colour. */
  id: string;
  name?: string | null;
  /** The org's logo or the site's icon; absent falls back to initials. */
  image?: string | null;
  /** Size and anything else common to both, e.g. `size-10`. */
  className?: string;
  /** The initials square's tint. Defaults to the id's own; the sidebar passes the brand's. */
  fallbackClassName?: string;
  /**
   * Put the image on a plate: a white tile, and the hairline that gives
   * it an edge. On by default, for an organisation logo — those are
   * supplied as a mark for a light header, so a dark one would disappear
   * on a dark background.
   *
   * A site passes false. Its mark is the site's favicon, drawn to sit on
   * whatever chrome a browser gives it — already transparent and legible
   * either way — so a tile behind it is just a tile, and a line around it
   * is a box around the logo.
   */
  plate?: boolean;
}) {
  const src = image?.trim();
  // No hairline on a supplied image that is not on a plate: a site's mark
  // is its favicon, drawn transparent for browser chrome, and a line
  // around it reads as a box drawn around the logo rather than an edge
  // the logo has. The initials fallback below IS a tile and keeps its own.
  if (src) return <Avatar square src={src} alt="" outline={plate} className={clsx(className, plate && 'bg-white')} />;
  return <Avatar square initials={initialsOf(name || id)} alt="" className={clsx(className, fallbackClassName ?? tintOf(id))} />;
}
